import {createSelector, createEntityAdapter, EntityState} from '@reduxjs/toolkit'
import {
  FetchBaseQueryMeta,
  createApi,
  fetchBaseQuery,
  FetchArgs,
  BaseQueryFn,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import {RootState} from '../../../data/redux/Store'
import {AUTH_TOKEN_KEY, env} from '../../../data/constants'
import {
  IFinancierPreferences,
  IPaginatedList,
  IPaginationMetadata,
  ISendCompanyEmail,
} from '../shared/interfaces'
import {INameIdPair} from '../shared/interfaces'
import {
  IAssignAnalyst,
  ICompanyContact,
  ICompanyDetails,
  ICompanyDocumentFolders,
  ICompanyFinancingAsks,
  IFinancialData,
  IManagementTeam,
  IRankedCompanyDetails,
  ISearchCompanyDocument,
  ISearchFinancialData,
  ISearchFinancierData,
  ISummaryStatistics,
  IRiskProfile,
} from './interfaces'
import {ICompanyDocumentMetaData} from '../documents'
import {ISearchFolder} from './interfaces/ISearchFolder'
import {ISearchEntities} from '../shared/interfaces/ISearchEntities'
import {IRole} from './interfaces/IRole'
import {IServiceProviderDetails} from '../serviceProviders/interfaces'
import {
  IEmailRequest,
  IFinancierCompanyDetails,
  IFinancierLetterOfIntent,
  IFinancierNotInterested,
  IFinancierRestoreCompanies,
  IFinancierSignedNda,
} from './interfaces/IFinancierCompanyDetails'

const CompaniesAdapter = createEntityAdapter<ICompanyDetails>({
  selectId: (company) => company.id,
})
const RankedCompaniesAdapter = createEntityAdapter<IRankedCompanyDetails>({
  selectId: (rankedCompany) => rankedCompany.company.id,
})
const SectorsAdapter = createEntityAdapter<INameIdPair>({
  selectId: (sector) => sector.id,
})
const FinancingTypesAdapter = createEntityAdapter<INameIdPair>({
  selectId: (financingType) => financingType.id,
})
const CompanyFinancialsAdapter = createEntityAdapter<ICompanyFinancingAsks>({
  selectId: (companyFinancials) => companyFinancials.id,
})
const CompanyContactsAdapter = createEntityAdapter<ICompanyContact>({
  selectId: (companyContacts) => companyContacts.id,
})
const CompanyDocumentsAdapter = createEntityAdapter<ICompanyDocumentMetaData>({
  selectId: (companyDocuments) => companyDocuments.id,
})
const DocumentFoldersAdapter = createEntityAdapter<ICompanyDocumentFolders>({
  selectId: (companyDocumentFolders) => companyDocumentFolders.id,
})

const RolesAdapter = createEntityAdapter<IRole>({selectId: (roles) => roles.id})

const FinancialDataAdapter = createEntityAdapter<IFinancialData>({
  selectId: (FinancialData) => FinancialData.id,
  sortComparer: (a, b) => a.year - b.year,
})
const FinancierCompaniesAdapter = createEntityAdapter<IFinancierCompanyDetails>({
  selectId: (company) => company.id,
})

const ManagementTeamAdapter = createEntityAdapter<IManagementTeam>({
  selectId: (company) => company.id,
})

const companiesInitialState = CompaniesAdapter.getInitialState()
const rankedCompaniesInitialState = RankedCompaniesAdapter.getInitialState()
const companyDocumentsInitialState = CompanyDocumentsAdapter.getInitialState()
const DocumentFoldersInitialState = DocumentFoldersAdapter.getInitialState()
const companyFinancialsInitialState = CompanyFinancialsAdapter.getInitialState()
const companyContactsInitialState = CompanyContactsAdapter.getInitialState()
const sectorsInitialState = SectorsAdapter.getInitialState()
const financingTypesInitialState = FinancingTypesAdapter.getInitialState()
const rolesInitialState = RolesAdapter.getInitialState()
const financialDataInitialState = FinancialDataAdapter.getInitialState()
const financierCompaniesInitialState = FinancierCompaniesAdapter.getInitialState()
const managementTeamInitialState = ManagementTeamAdapter.getInitialState()

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = fetchBaseQuery({
  baseUrl: env.Company,
  validateStatus: (response, result) => {
    if (result && result.isError) return false
    if (response.status >= 200 && response.status <= 300) return true
    if (response.status >= 400 && response.status <= 500) return false
    return false
  },
  prepareHeaders: (headers, {getState}) => {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEY)
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }
    return headers
  },
})

const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions)

  if (result.error) {
    const {status} = result.error

    throw new Error(`Error status: ${status}`)
  }

  return result
}

export const CompaniesApiSlice = createApi({
  reducerPath: 'companiesApi',
  baseQuery: baseQuery,
  tagTypes: [
    'Company',
    'CompanyDetails',
    'CompanyProduct',
    'CompanyFinancials',
    'CompanyContact',
    'Sectors',
    'FinancingTypes',
    'CompanyDocument',
    'CompanyDocumentFolder',
    'Role',
    'FinancialData',
    'Nda',
    'Letter',
    'ManagementTeam',
  ],
  endpoints: (builder) => ({
    getCompanies: builder.query<
      {entities: EntityState<ICompanyDetails>; paginationMetadata: IPaginationMetadata},
      ISearchEntities
    >({
      query: (searchParams) => {
        const params = new URLSearchParams()
        if (searchParams.page) params.append('Page', searchParams.page.toString())
        if (searchParams.pageSize) params.append('PageSize', searchParams.pageSize.toString())
        if (searchParams.searchTerm) params.append('SearchTerm', searchParams.searchTerm)
        if (searchParams.minInvestmentAsk)
          params.append('minInvestmentAsk', searchParams.minInvestmentAsk.toString())
        if (searchParams.maxInvestmentAsk)
          params.append('maxInvestmentAsk', searchParams.maxInvestmentAsk.toString())
        if (searchParams.investmentAskCurrency)
          params.append('investmentAskCurrency', searchParams.investmentAskCurrency)
        if (searchParams.countries) {
          searchParams.countries.forEach((country) => params.append('Countries', country))
        }
        if (searchParams.minYearsInOperation)
          params.append('minYearsInOperation', searchParams.minYearsInOperation.toString())
        if (searchParams.maxYearsInOperation)
          params.append('maxYearsInOperation', searchParams.maxYearsInOperation.toString())
        if (searchParams.sectors) {
          searchParams.sectors.forEach((sector) => params.append('Sectors', sector))
        }
        if (searchParams.financingTypes) {
          searchParams.financingTypes.forEach((type) => params.append('FinancingTypes', type))
        }
        if (searchParams.onboardingStatus) {
          params.append('onboardingStatus', searchParams.onboardingStatus)
        }
        if (searchParams.averageAnnualRevenue) {
          params.append('AverageAnnualRevenue', searchParams.averageAnnualRevenue)
        }
        if (searchParams.averageNetProfitMargin) {
          params.append('AverageNetProfitMargin', searchParams.averageNetProfitMargin)
        }
        if (searchParams.averageEbitda) {
          params.append('AverageEbitda', searchParams.averageEbitda)
        }
        if (searchParams.assetValue) {
          params.append('AssetValue', searchParams.assetValue)
        }
        if (searchParams.AnalystId) {
          params.append('AnalystId', searchParams.AnalystId)
        }
        if (searchParams.minRiskScore) {
          params.append('minRiskScore', searchParams.minRiskScore.toString())
        }
        if (searchParams.maxRiskScore) {
          params.append('maxRiskScore', searchParams.maxRiskScore.toString())
        }

        return {
          url: '/companies',
          params: params,
        }
      },
      transformResponse: (response: any, meta: FetchBaseQueryMeta) => {
        // console.log('response', response)
        const entities = CompaniesAdapter.setAll(companiesInitialState, response.data)
        // console.log('entities', entities)
        const paginationMetadata = response.paginationMetadata
        const pagination: IPaginationMetadata = paginationMetadata
          ? {
              totalCount: paginationMetadata.totalItems || 0,
              page: paginationMetadata.currentPage || 1,
              pageSize: paginationMetadata.pageSize || 9,
              hasNextPage: paginationMetadata.hasNextPage || false,
              hasPreviousPage: paginationMetadata.hasPreviousPage || false,
              totalPages: paginationMetadata.totalPages,
            }
          : {
              totalCount: 0,
              page: 1,
              pageSize: 9,
              hasNextPage: false,
              hasPreviousPage: false,
            }
        return {
          entities: entities,
          paginationMetadata: pagination,
        }
      },
      providesTags: (result: {entities: EntityState<ICompanyDetails>} | undefined) => {
        if (result?.entities.ids) {
          return [
            {type: 'Company', id: 'LIST'},
            ...result.entities.ids.map((id) => ({type: 'Company' as const, id})),
          ]
        } else return [{type: 'Company', id: 'LIST'}]
      },
    }),
    getRankedCompanies: builder.query<
      {entities: EntityState<IRankedCompanyDetails>; paginationMetadata: IPaginatedList},
      IFinancierPreferences
    >({
      query: (searchParams) => {
        const params = new URLSearchParams()
        // if (searchParams.page) params.append('Page', searchParams.page.toString())
        // if (searchParams.pageSize) params.append('PageSize', searchParams.pageSize.toString())

        if (searchParams.searchTerm) params.append('SearchTerm', searchParams.searchTerm)

        if (searchParams.minInvestmentAsk)
          params.append('minInvestmentAsk', searchParams.minInvestmentAsk.toString())
        if (searchParams.maxInvestmentAsk)
          params.append('maxInvestmentAsk', searchParams.maxInvestmentAsk.toString())
        if (searchParams.investmentAskCurrency)
          params.append('investmentAskCurrency', searchParams.investmentAskCurrency)
        if (searchParams.countries) {
          searchParams.countries.forEach((country) => params.append('Countries', country))
        }
        if (searchParams.onboardingStatus) {
          params.append('onboardingStatus', searchParams.onboardingStatus)
        }
        if (searchParams.minYearsInOperation)
          params.append('minYearsInOperation', searchParams.minYearsInOperation.toString())
        if (searchParams.maxYearsInOperation)
          params.append('maxYearsInOperation', searchParams.maxYearsInOperation.toString())
        if (searchParams.sectors) {
          searchParams.sectors.forEach((sector) => params.append('Sectors', sector))
        }
        if (searchParams.financingTypes) {
          searchParams.financingTypes.forEach((type) => params.append('FinancingTypes', type))
        }

        if (searchParams.averageAnnualRevenue) {
          params.append('AverageAnnualRevenue', searchParams.averageAnnualRevenue.toString())
        }
        if (searchParams.averageNetProfitMargin) {
          params.append('AverageNetProfitMargin', searchParams.averageNetProfitMargin.toString())
        }
        if (searchParams.averageEbitda) {
          params.append('AverageEbitda', searchParams.averageEbitda.toString())
        }
        if (searchParams.assetValue) {
          params.append('AssetValue', searchParams.assetValue)
        }
        
        if (searchParams.minRiskScore) {
          params.append('minRiskScore', searchParams.minRiskScore.toString())
        }
        if (searchParams.maxRiskScore) {
          params.append('maxRiskScore', searchParams.maxRiskScore.toString())
        }

        return {
          url: 'companies/ranked',
          params: params,
        }
      },
      transformResponse: (response: any, meta: FetchBaseQueryMeta) => {
        // console.log('rankedResponse', response)
        const entities = RankedCompaniesAdapter.setAll(rankedCompaniesInitialState, response)
        // console.log('rankedEntities', entities)
        const paginationMetadata = response.paginationMetadata
        const pagination: IPaginatedList = paginationMetadata
          ? {
              totalCount: paginationMetadata.totalItems || 0,
              page: paginationMetadata.currentPage || 1,
              pageSize: paginationMetadata.pageSize || 9,
              totalPages: paginationMetadata.totalPages,
            }
          : {
              totalCount: 0,
              page: 1,
              pageSize: 9,
            }
        return {
          entities: entities,
          paginationMetadata: pagination,
        }
      },
      providesTags: (result: {entities: EntityState<IRankedCompanyDetails>} | undefined) => {
        if (result?.entities.ids) {
          return [
            {type: 'Company', id: 'LIST'},
            ...result.entities.ids.map((id) => ({type: 'Company' as const, id})),
          ]
        } else return [{type: 'Company', id: 'LIST'}]
      },
    }),
    getCompanyDetails: builder.query<EntityState<ICompanyDetails>, string>({
      query: (id) => `/companies/${id}`,
      transformResponse: (res: any) => {
        const company: ICompanyDetails = {
          ...res,
          averageAnnualRevenue: res.averageFinancialData?.averageAnnualRevenue,
          averageAnnualRevenueCurrency: res.averageFinancialData?.averageAnnualRevenueCurrency,
          averageNetProfitMargin: res.averageFinancialData?.averageNetProfitMargin,
          averageNetProfit: res.averageFinancialData?.averageNetProfit,
          averageEbitda: res.averageFinancialData?.averageEbitda,
          averageEbitdaMargin: res.averageFinancialData?.averageEbitdaMargin,
          averageGrossProfitMargin: res.averageFinancialData?.averageGrossProfitMargin,
        }
        return CompaniesAdapter.setOne(companiesInitialState, company)
      },
      providesTags: (result, _error, id) => [{type: 'CompanyDetails', id}],
    }),
    addNewCompany: builder.mutation({
      query: (initialCompanyData) => ({
        url: '/companies',
        method: 'POST',
        body: initialCompanyData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Company', id: 'LIST'}],
    }),
    updateCompany: builder.mutation({
      query: (updatedCompanyData) => ({
        url: `/companies/${updatedCompanyData.id}`,
        method: 'PUT',
        body: updatedCompanyData,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Company', id: arg.id},
        {type: 'CompanyDetails', id: arg.id},
      ],
    }),
    deleteCompany: builder.mutation({
      query: (id) => ({
        url: `/companies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Company', id: arg.id},
        {type: 'CompanyDetails', id: arg.id},
      ],
    }),
    addNewCompanyFinancials: builder.mutation({
      query: (CompanyFinancial) => ({
        url: '/companies/financing/asks',
        method: 'POST',
        body: {
          ...CompanyFinancial,
        },
      }),
      invalidatesTags: [{type: 'CompanyFinancials', id: 'LIST'}],
    }),
    getCompanyFinancials: builder.query<EntityState<ICompanyFinancingAsks>, string>({
      query: (id) => ({
        url: `/companies/financing/asks/?companyId=${id}`,
      }),
      transformResponse: (res: any) => {
        return CompanyFinancialsAdapter.setAll(companyFinancialsInitialState, res)
      },
      providesTags: (result: EntityState<ICompanyFinancingAsks> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'CompanyFinancials', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'CompanyFinancials' as const, id})),
          ]
        } else return [{type: 'CompanyFinancials', id: 'LIST'}]
      },
    }),
    updateCompanyFinancials: builder.mutation({
      query: (updatedCompanyFinancialsData) => ({
        url: `/companies/financing/asks/${updatedCompanyFinancialsData.id}`,
        method: 'PUT',
        body: updatedCompanyFinancialsData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'CompanyFinancials', id: arg.id}],
      async onQueryStarted({id, ...rest}, {dispatch, queryFulfilled}) {
        const putResult = dispatch(
          CompaniesApiSlice.util.updateQueryData('getCompanyFinancials', id, (draft) => {
            let companyFinancials = draft.entities[id]
            if (companyFinancials) {
              Object.assign(companyFinancials, rest)
            }
          })
        )
        try {
          await queryFulfilled
        } catch {
          putResult.undo()
        }
      },
    }),
    getCompanyContacts: builder.query<EntityState<ICompanyContact>, string>({
      query: (id) => ({
        url: `/companies/contacts/?companyId=${id}`,
      }),
      transformResponse: (res: any) => {
        const transformedResponse = res.data.map((contact: any) => ({
          companyContactPersonAddress: contact.address,
          companyContactPersonName: contact.name,
          companyContactPersonEmail: contact.email,
          companyContactPersonTelephone: contact.telephone,
          companyContactPersonPicture: contact.picture,
          companyId: contact.companyId,
          id: contact.id,
        }))
        return CompanyContactsAdapter.setAll(companyContactsInitialState, transformedResponse)
      },
      providesTags: (result: EntityState<ICompanyContact> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'CompanyContact', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'CompanyContact' as const, id})),
          ]
        } else return [{type: 'CompanyContact', id: 'LIST'}]
      },
    }),
    updateCompanyContact: builder.mutation({
      query: (updatedCompanyContactData) => ({
        url: `/companies/contacts/${updatedCompanyContactData.id}`,
        method: 'PUT',
        body: updatedCompanyContactData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'CompanyContact', id: arg.id}],
      async onQueryStarted({id, ...rest}, {dispatch, queryFulfilled}) {
        const putResult = dispatch(
          CompaniesApiSlice.util.updateQueryData('getCompanyContacts', id, (draft) => {
            let companyContact = draft.entities[id]
            if (companyContact) {
              Object.assign(companyContact, rest)
            }
          })
        )
        try {
          await queryFulfilled
        } catch {
          putResult.undo()
        }
      },
    }),
    getSectors: builder.query<EntityState<INameIdPair>, void>({
      query: () => ({
        url: `/sectors`,
      }),
      transformResponse: (res: any) => {
        return SectorsAdapter.setAll(sectorsInitialState, res)
      },
      providesTags: (result: EntityState<INameIdPair> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'Sectors', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'Sectors' as const, id})),
          ]
        } else return [{type: 'Sectors', id: 'LIST'}]
      },
    }),
    addNewSector: builder.mutation({
      query: (sectorName) => ({
        url: '/sectors',
        method: 'POST',
        body: sectorName,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Sectors', id: 'LIST'}],
    }),
    updateSector: builder.mutation({
      query: (sectorData) => ({
        url: `/sectors/${sectorData.id}`,
        method: 'PUT',
        body: sectorData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Sectors', id: arg.id}],
    }),
    deleteSector: builder.mutation({
      query: (id) => ({
        url: `/sectors/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Sectors', id: arg.id}],
    }),
    getFinancingTypes: builder.query<EntityState<INameIdPair>, void>({
      query: () => ({
        url: `/financing/types`,
      }),
      transformResponse: (res: any) => {
        return FinancingTypesAdapter.setAll(financingTypesInitialState, res)
      },
      providesTags: (result: EntityState<INameIdPair> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'FinancingTypes', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'FinancingTypes' as const, id})),
          ]
        } else return [{type: 'FinancingTypes', id: 'LIST'}]
      },
    }),
    addNewFinancingType: builder.mutation({
      query: (financingTypeName) => ({
        url: '/financing/types',
        method: 'POST',
        body: financingTypeName,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'FinancingTypes', id: 'LIST'}],
    }),
    updateFinancingType: builder.mutation({
      query: (financingTypeData) => ({
        url: `/financing/types/${financingTypeData.id}`,
        method: 'PUT',
        body: financingTypeData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'FinancingTypes', id: arg.id}],
    }),
    deleteFinancingType: builder.mutation({
      query: (id) => ({
        url: `/financing/types/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'FinancingTypes', id: arg.id}],
    }),
    addNewCompanyDocument: builder.mutation({
      query: (documentMetaData) => ({
        url: '/documents',
        method: 'POST',
        body: documentMetaData,
      }),
      invalidatesTags: (_result, _error) => [
        {type: 'CompanyDocument', id: 'LIST'},
        {type: 'CompanyDocumentFolder', id: 'LIST'},
      ],
    }),
    getCompanyDocuments: builder.query<
      EntityState<ICompanyDocumentMetaData>,
      ISearchCompanyDocument
    >({
      query: ({companyId, folderId, searchTerm, documentCategory}) => {
        const params: Record<string, any> = {companyId}

        // Only include folderId if it's explicitly provided (not undefined or empty string)
        if (folderId !== undefined && folderId !== '') {
          params.folderId = folderId
        }
        if (searchTerm) params.searchTerm = searchTerm
        if (documentCategory) params.documentCategory = documentCategory

        return {
          url: `/documents`,
          params,
        }
      },

      transformResponse: (response: {data: ICompanyDocumentMetaData[]}) => {
        return CompanyDocumentsAdapter.setAll(companyDocumentsInitialState, response.data)
      },

      providesTags: (result) =>
        result?.ids
          ? [
              {type: 'CompanyDocument', id: 'LIST'},
              ...result.ids.map((id) => ({type: 'CompanyDocument' as const, id})),
            ]
          : [{type: 'CompanyDocument', id: 'LIST'}],
    }),
    deleteCompanyDocument: builder.mutation({
      query: ({id, folderId}) => ({
        url: `/documents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'CompanyDocument', id: 'LIST'},
        {type: 'CompanyDocumentFolder', id: 'LIST'},
      ],
    }),
    getDocumentFolders: builder.query<EntityState<ICompanyDocumentFolders>, ISearchFolder>({
      query: ({id, companyId, searchTerm, includeOnlyParentFolders, parentFolderId}) => ({
        url: `/folders/`,
        params: {id, companyId, searchTerm, parentFolderId, includeOnlyParentFolders},
      }),
      transformResponse: (res: any) => {
        return DocumentFoldersAdapter.setAll(DocumentFoldersInitialState, res.data)
      },
      providesTags: (result: EntityState<ICompanyDocumentFolders> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'CompanyDocumentFolder', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'CompanyDocumentFolder' as const, id})),
          ]
        } else return [{type: 'CompanyDocumentFolder', id: 'LIST'}]
      },
    }),
    addNewDocumentFolder: builder.mutation({
      query: (documentFolderData) => ({
        url: '/folders',
        method: 'POST',
        body: documentFolderData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'CompanyDocumentFolder', id: 'LIST'}],
    }),
    updateDocumentFolder: builder.mutation({
      query: (documentFolderData) => ({
        url: `/folders/${documentFolderData.id}`,
        method: 'PUT',
        body: documentFolderData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'CompanyDocumentFolder', id: 'LIST'}],
    }),
    deleteDocumentFolder: builder.mutation({
      query: (id) => ({
        url: `/folders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'CompanyDocumentFolder', id: arg.id}],
    }),
    checkNameAvailability: builder.mutation({
      query: ({name}) => ({
        url: '/check-company-name',
        body: {name},
        method: 'POST',
      }),
    }),
    getRoles: builder.query<EntityState<IRole>, void>({
      query: (name?) => ({
        url: `/roles`,
        body: name,
      }),
      transformResponse: (res: any) => {
        return RolesAdapter.setAll(rolesInitialState, res)
      },
      providesTags: (result: EntityState<IRole> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'Role', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'Role' as const, id})),
          ]
        } else return [{type: 'Role', id: 'LIST'}]
      },
    }),
    triggerRoles: builder.query<EntityState<IRole>, string>({
      query: (name) => ({
        url: `/roles`,
        params: {name}, // Send the name as a query parameter
      }),
      transformResponse: (res: {data: IRole[]}) => {
        return RolesAdapter.setAll(rolesInitialState, res.data) // Extract actual role list
      },
    }),

    addNewRole: builder.mutation({
      query: (roleName) => ({
        url: '/roles',
        method: 'POST',
        body: roleName,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Role', id: 'LIST'}],
    }),
    updateRole: builder.mutation({
      query: (roleData) => ({
        url: `/roles/${roleData.id}`,
        method: 'PUT',
        body: roleData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Role', id: arg.id}],
    }),
    deleteRole: builder.mutation({
      query: (id) => ({
        url: `/roles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'Role', id: arg.id}],
    }),

    getFinancialData: builder.query<EntityState<IFinancialData>, ISearchFinancialData>({
      query: (searchParams) => {
        const params = new URLSearchParams()
        if (searchParams.companyId) params.append('CompanyId', searchParams.companyId)
        if (searchParams.minAnnualRevenue)
          params.append('MinimumAnnualRevenue', searchParams.minAnnualRevenue)
        if (searchParams.maxAnnualRevenue)
          params.append('MaximumAnnualRevenue', searchParams.maxAnnualRevenue)
        if (searchParams.minOperatingExpenses)
          params.append('MinimumOperatingExpenses', searchParams.minOperatingExpenses)
        if (searchParams.maxOperatingExpenses)
          params.append('MaximumOperatingExpenses', searchParams.maxOperatingExpenses)
        if (searchParams.minGrossProfit)
          params.append('MinimumGrossProfit', searchParams.minGrossProfit)
        if (searchParams.maxGrossProfit)
          params.append('MaximumGrossProfit', searchParams.maxGrossProfit)
        if (searchParams.minNetProfit) {
          params.append('MinimumNetProfit', searchParams.minNetProfit)
        }
        if (searchParams.maxNetProfit) params.append('MaximumNetProfit', searchParams.maxNetProfit)
        if (searchParams.minNetProfitMargin)
          params.append('MinimumNetProfitMargin', searchParams.minNetProfitMargin)
        if (searchParams.maxNetProfitMargin) {
          params.append('MaximumNetProfitMargin', searchParams.maxNetProfitMargin)
        }
        if (searchParams.minGrossProfitMargin) {
          params.append('MinimumGrossProfitMargin', searchParams.minGrossProfitMargin)
        }
        if (searchParams.maxGrossProfitMargin) {
          params.append('MaximumGrossProfitMargin', searchParams.maxGrossProfitMargin)
        }
        if (searchParams.minEbitda) {
          params.append('MinimumEbitda', searchParams.minEbitda)
        }
        if (searchParams.maxEbitda) {
          params.append('MaximumEbitda', searchParams.maxEbitda)
        }
        if (searchParams.startingYear) {
          params.append('MinimumYear', searchParams.startingYear)
        }
        if (searchParams.endingYear) {
          params.append('MaximumYear', searchParams.endingYear)
        }

        return {
          url: '/financial-data',
          params: params,
        }
      },
      transformResponse: (res: any) => {
        const data = res?.data ?? []
        return FinancialDataAdapter.setAll(financialDataInitialState, data)
      },
      providesTags: (result: EntityState<IFinancialData> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'FinancialData', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'FinancialData' as const, id})),
          ]
        } else return [{type: 'FinancialData', id: 'LIST'}]
      },
    }),
    getFinancialDataDetails: builder.query<EntityState<IFinancialData>, string>({
      query: (id) => `/financial-data/${id}`,
      transformResponse: (res: any) => {
        return FinancialDataAdapter.setOne(financialDataInitialState, res)
      },
      providesTags: (result, _error, id) => [{type: 'FinancialData', id}],
    }),
    AddNewFinancialData: builder.mutation({
      query: (initialFinancialData) => ({
        url: `/financial-data`,
        method: 'POST',
        body: initialFinancialData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'FinancialData', id: 'LIST'}],
    }),
    updateFinancialData: builder.mutation({
      query: (updatedFinancialData) => ({
        url: `/financial-data/${updatedFinancialData.id}`,
        method: 'PUT',
        body: updatedFinancialData,
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'FinancialData', id: arg.id}],
    }),
    deleteFinancialData: builder.mutation({
      query: (id) => ({
        url: `/financial-data/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{type: 'FinancialData', id: arg.id}],
    }),

    getAllFinancierCompanies: builder.query<
      {entities: EntityState<IFinancierCompanyDetails>; paginationMetadata: IPaginationMetadata},
      ISearchEntities
    >({
      query: (searchParams) => {
        const params = new URLSearchParams()
        if (searchParams.page) params.append('Page', searchParams.page.toString())
        if (searchParams.pageSize) params.append('PageSize', searchParams.pageSize.toString())
        if (searchParams.searchTerm) params.append('SearchTerm', searchParams.searchTerm)

        return {
          url: `/all`,
          params: params,
        }
      },
      transformResponse: (response: any) => {
        const entities = FinancierCompaniesAdapter.setAll(
          financierCompaniesInitialState,
          response.data
        )
        const paginationMetadata = response.paginationMetadata
        const pagination: IPaginationMetadata = paginationMetadata
          ? {
              totalCount: paginationMetadata.totalItems || 0,
              page: paginationMetadata.currentPage || 1,
              pageSize: paginationMetadata.pageSize || 9,
              hasNextPage: paginationMetadata.hasNextPage || false,
              hasPreviousPage: paginationMetadata.hasPreviousPage || false,
            }
          : {
              totalCount: 0,
              page: 1,
              pageSize: 9,
              hasNextPage: false,
              hasPreviousPage: false,
            }
        return {
          entities,
          paginationMetadata: pagination,
        }
      },
      providesTags: (result) => {
        if (result?.entities.ids) {
          return [
            {type: 'Company', id: 'LIST'},
            ...result.entities.ids.map((id) => ({type: 'Company' as const, id})),
          ]
        }
        return [{type: 'Company', id: 'LIST'}]
      },
    }),
    getNdaSigned: builder.query<
      {
        entities: EntityState<ICompanyDetails>
        paginationMetadata: IPaginationMetadata
      },
      ISearchFinancierData
    >({
      query: (searchParams) => {
        const params = new URLSearchParams()
        if (searchParams.financierId) params.append('FinancierId', searchParams.financierId)

        return {
          url: `financier-companies/nda`,
          method: 'GET',
          params: params,
        }
      },
      transformResponse: (response: any) => {
        const entities = CompaniesAdapter.setAll(companiesInitialState, response.data)
        const paginationMetadata = response.paginationMetadata
        const pagination: IPaginationMetadata = paginationMetadata
          ? {
              totalCount: paginationMetadata.totalItems || 0,
              page: paginationMetadata.currentPage || 1,
              pageSize: paginationMetadata.pageSize || 9,
              hasNextPage: paginationMetadata.hasNextPage || false,
              hasPreviousPage: paginationMetadata.hasPreviousPage || false,
            }
          : {
              totalCount: 0,
              page: 1,
              pageSize: 9,
              hasNextPage: false,
              hasPreviousPage: false,
            }
        return {
          entities: entities,
          paginationMetadata: pagination,
        }
      },
      providesTags: (result: {entities: EntityState<ICompanyDetails>} | undefined) => {
        if (result?.entities.ids) {
          return [
            {type: 'Company', id: 'LIST'},
            ...result.entities.ids.map((id) => ({type: 'Company' as const, id})),
          ]
        } else return [{type: 'Company', id: 'LIST'}]
      },
    }),
    getFinancierNdas: builder.query<IFinancierSignedNda[], ISearchFinancierData>({
      query: (searchParams) => {
        const params = new URLSearchParams()

        if (searchParams.financierId) params.append('FinancierId', searchParams.financierId)
        if (searchParams.companyId) params.append('CompanyId', searchParams.companyId)

        return {
          url: `financier-companies/ndas`,
          method: 'GET',
          params: params,
        }
      },
      transformResponse: (response: any) => {
        return response.data || []
      },
      providesTags: (result) =>
        result
          ? [{type: 'Nda', id: 'LIST'}, ...result.map(({id}) => ({type: 'Nda' as const, id}))]
          : [{type: 'Nda', id: 'LIST'}],
    }),

    addNdaSigned: builder.mutation<
      IFinancierSignedNda,
      {companyId: string; financierId: string; ndaUrl: string}
    >({
      query: (data) => ({
        url: `financier-companies/sign-Nda`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Company', id: 'LIST'},
        {type: 'Nda', id: 'LIST'},
        {type: 'Company', id: arg.companyId},
      ],
    }),
    getFinancierLetters: builder.query<IFinancierLetterOfIntent[], ISearchFinancierData>({
      query: (searchParams) => {
        const params = new URLSearchParams()

        if (searchParams.financierId) params.append('FinancierId', searchParams.financierId)
        if (searchParams.companyId) params.append('CompanyId', searchParams.companyId)
        return {
          url: `financier-companies/letters`,
          method: 'GET',
          params: params,
        }
      },
      transformResponse: (response: any) => {
        return response.data || []
      },
      providesTags: (result) =>
        result
          ? [{type: 'Letter', id: 'LIST'}, ...result.map(({id}) => ({type: 'Nda' as const, id}))]
          : [{type: 'Letter', id: 'LIST'}],
    }),
    getLetterOfIntentDetails: builder.query<IFinancierLetterOfIntent[], {financierId: string}>({
      query: (searchParams) => {
        const params = new URLSearchParams()

        if (searchParams.financierId) params.append('FinancierId', searchParams.financierId)

        return {
          url: `financier-companies/lettersOfIntent`,
          method: 'GET',
          params: params,
        }
      },
      transformResponse: (response: any) => {
        return response.data || []
      },
      providesTags: (result) =>
        result
          ? [
              {type: 'Company', id: 'LIST'},
              ...result.map(({id}) => ({type: 'Company' as const, id})),
            ]
          : [{type: 'Company', id: 'LIST'}],
    }),

    addLetterOfIntent: builder.mutation<
      IFinancierLetterOfIntent,
      {
        lettersOfIntent: {
          companyId: string
          financierId: string
          letterId: string
        }
        emailRequest: IEmailRequest[]
      }
    >({
      query: (data) => ({
        url: `financier-companies/letterOfIntent/send`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Company', id: 'LIST'},
        {type: 'Company', id: arg.lettersOfIntent.companyId},
      ],
    }),

    getArchivedCompany: builder.query<ICompanyDetails[], {financierId: string}>({
      query: (searchParams) => {
        const params = new URLSearchParams()

        if (searchParams.financierId) params.append('FinancierId', searchParams.financierId)

        return {
          url: `financier-companies/archived`,
          method: 'GET',
          params: params,
        }
      },
      transformResponse: (response: any) => {
        return response.data || []
      },
      providesTags: (result) =>
        result
          ? [
              {type: 'Company', id: 'LIST'},
              ...result.map(({id}) => ({type: 'Company' as const, id})),
            ]
          : [{type: 'Company', id: 'LIST'}],
    }),

    addArchivedCompany: builder.mutation<
      IFinancierNotInterested,
      {
        companyId: string
        financierId: string
        reason: string
        description: string
      }
    >({
      query: (data) => ({
        url: `financier-companies/archive`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Company', id: 'LIST'},
        {type: 'Company', id: arg.companyId},
      ],
    }),

    restoreArchivedCompany: builder.mutation<void, IFinancierRestoreCompanies>({
      query: (restoreData) => ({
        url: 'financier-companies/restore-archived-company',
        method: 'POST',
        body: restoreData,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Company', id: 'LIST'},
        {type: 'CompanyDetails', id: arg.companyId},
      ],
    }),

    assignAnalyst: builder.mutation<void, IAssignAnalyst>({
      query: (data) => ({
        url: `companies/assign`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'Company', id: 'LIST'},
        {type: 'Company', id: arg.companyId},
        {type: 'CompanyDetails', id: arg.companyId},
      ],
    }),
    getManagementTeam: builder.query<EntityState<IManagementTeam>, string>({
      query: (companyId) => ({
        url: `/management`,
        params: {companyId},
      }),
      transformResponse: (response: any) => {
        const data = response?.data ?? []
        return ManagementTeamAdapter.setAll(managementTeamInitialState, data)
      },
      providesTags: (result: EntityState<IManagementTeam> | undefined) => {
        if (result?.ids) {
          return [
            {type: 'ManagementTeam', id: 'LIST'},
            ...result.ids.map((id) => ({type: 'ManagementTeam' as const, id})),
          ]
        } else return [{type: 'ManagementTeam', id: 'LIST'}]
      },
    }),
    addManagementTeam: builder.mutation<IManagementTeam, Omit<IManagementTeam, 'id'>>({
      query: (data) => ({
        url: '/management',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'ManagementTeam', id: 'LIST'},
        {type: 'Company', id: arg.companyId},
      ],
    }),
    updateManagementTeam: builder.mutation<
      IManagementTeam,
      {
        id: string
        companyId: string
        managementTeam: Partial<IManagementTeam>
      }
    >({
      query: (data) => ({
        url: `/management/${data.id}`,
        method: 'PUT',
        body: data.managementTeam,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'ManagementTeam', id: arg.id},
        {type: 'ManagementTeam', id: 'LIST'},
      ],
    }),
    deleteManagementTeam: builder.mutation<void, {id: string; companyId: string}>({
      query: ({id}) => ({
        url: `/management/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        {type: 'ManagementTeam', id: arg.id},
        {type: 'ManagementTeam', id: 'LIST'},
      ],
    }),
    generateInviteToken: builder.mutation<
      {token: string},
      {sectors: string[]; minInvestmentAsk: number; maxInvestmentAsk: number}
    >({
      query: (preferences) => ({
        url: `companies/generate-invite-token`,
        method: 'POST',
        body: preferences,
      }),
    }),
    getSummaryStatistics: builder.query<ISummaryStatistics, void>({
      query: () => ({
        url: 'companies/summary-statistics',
        method: 'GET',
      }),
    }),
    uploadOCAAssessment: builder.mutation<
      {success: boolean; data: any},
      {companyId: string; excelFile: File; assessmentDocumentId: string}
    >({
      query: ({companyId, excelFile, assessmentDocumentId}) => {
        const formData = new FormData()
        formData.append('excelFile', excelFile)
        formData.append('assessmentDocumentId', assessmentDocumentId)

        return {
          url: `/RiskAssessment/companies/${companyId}/oca-assessment`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: (result, error, {companyId}) => [
        {type: 'Company', id: companyId},
        {type: 'CompanyDetails', id: companyId},
        {type: 'CompanyDocument', id: 'LIST'},
      ],
    }),
    getCompanyRiskProfile: builder.query<IRiskProfile, string>({
      query: (companyId) => ({
        url: `/RiskAssessment/companies/${companyId}/risk-profile`,
        method: 'GET',
      }),
      transformResponse: (response: {success: boolean; data: IRiskProfile}) => response.data,
      providesTags: (result, error, companyId) => [{type: 'Company', id: companyId}],
    }),
    sendCompanyEmails: builder.mutation<
      {message: string; recipientCount: number; companyCount: number; companyIds: string[]},
      ISendCompanyEmail
    >({
      query: (data) => ({
        url: 'companies/send-emails',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{type: 'Company', id: 'LIST'}],
    }),
  }),
})

export const {
  // Companies
  useGetCompaniesQuery,
  useGetCompanyDetailsQuery,
  useAddNewCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useCheckNameAvailabilityMutation,
  useGetRankedCompaniesQuery,
  useGenerateInviteTokenMutation,
  useLazyGetCompaniesQuery,
  // useLazyGenerateInviteTokenMutation,
  // Company Contacts
  useUpdateCompanyContactMutation,
  useGetCompanyContactsQuery,
  useGetSummaryStatisticsQuery,
  // Company Financials
  useGetCompanyFinancialsQuery,
  useUpdateCompanyFinancialsMutation,
  useAddNewCompanyFinancialsMutation,
  // Company Documents
  useAddNewCompanyDocumentMutation,
  useGetCompanyDocumentsQuery,
  useDeleteCompanyDocumentMutation,
  // Company Document Folders
  useAddNewDocumentFolderMutation,
  useUpdateDocumentFolderMutation,
  useGetDocumentFoldersQuery,
  useDeleteDocumentFolderMutation,
  // Company Sectors
  useGetSectorsQuery,
  useAddNewSectorMutation,
  useUpdateSectorMutation,
  useDeleteSectorMutation,
  // Financing Types
  useGetFinancingTypesQuery,
  useAddNewFinancingTypeMutation,
  useUpdateFinancingTypeMutation,
  useDeleteFinancingTypeMutation,
  //Roles
  useGetRolesQuery,
  useLazyTriggerRolesQuery,
  useAddNewRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  //FinancialData
  useGetFinancialDataQuery,
  useGetFinancialDataDetailsQuery,
  useAddNewFinancialDataMutation,
  useUpdateFinancialDataMutation,
  useDeleteFinancialDataMutation,
  useGetAllFinancierCompaniesQuery,
  useGetNdaSignedQuery,
  useAddNdaSignedMutation,
  useGetFinancierNdasQuery,
  useGetLetterOfIntentDetailsQuery,
  useGetFinancierLettersQuery,
  useAddLetterOfIntentMutation,
  useGetArchivedCompanyQuery,
  useAddArchivedCompanyMutation,
  useAssignAnalystMutation,
  useRestoreArchivedCompanyMutation,
  //Management
  useGetManagementTeamQuery,
  useAddManagementTeamMutation,
  useUpdateManagementTeamMutation,
  useDeleteManagementTeamMutation,
  useUploadOCAAssessmentMutation,
  useGetCompanyRiskProfileQuery,
  useSendCompanyEmailsMutation,
} = CompaniesApiSlice

// Companies selector
export const selectCompaniesResult = (state: RootState, searchParams: ISearchEntities) =>
  CompaniesApiSlice.endpoints.getCompanies.select(searchParams)(state)

export const selectCompaniesData = (searchParams: ISearchEntities) =>
  createSelector(
    (state: RootState) => selectCompaniesResult(state, searchParams),
    (companiesResult) => companiesResult?.data?.entities ?? companiesInitialState
  )

export const createCompanySelectors = (searchParams: ISearchEntities) => {
  const selectData = selectCompaniesData(searchParams)
  return CompaniesAdapter.getSelectors(selectData)
}

// Custom Document Folders selectors
export const selectDocumentResult = (searchObject: ISearchFolder) =>
  CompaniesApiSlice.endpoints.getDocumentFolders.select(searchObject)

// creates memoized selector
const selectFoldersData = createSelector(
  [selectDocumentResult],
  (DocumentSelector) => (state: RootState) => {
    const result = DocumentSelector(state)
    return result.data ?? DocumentFoldersInitialState // Accessing the 'data' property here
  }
)

// Get selectors from adapter and use the custom selector to access the state
export const {selectById: selectFolderById, selectAll: selectAllFolders} =
  DocumentFoldersAdapter.getSelectors((state: RootState) => selectFoldersData(state)(state))

//Financier sectors selectors

export const selectSectorsResult = CompaniesApiSlice.endpoints.getSectors.select()

const selectSectorsData = createSelector(selectSectorsResult, (SectorsResult) => SectorsResult.data)

export const selectRolesResult = CompaniesApiSlice.endpoints.getRoles.select()

const selectRolesData = createSelector(selectRolesResult, (RolesResult) => RolesResult.data)

export const {
  selectAll: selectAllSectors,
  selectById: selectSectorById,
  selectIds: selectSectorIds,
} = SectorsAdapter.getSelectors(
  (state: RootState) => selectSectorsData(state) ?? sectorsInitialState
)

export const {
  selectAll: selectAllRoles,
  selectById: selectRoleById,
  selectIds: selectRoleIds,
} = RolesAdapter.getSelectors((state: RootState) => selectRolesData(state) ?? rolesInitialState)
